
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Search, Users } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import RecipeUrlForm from '@/components/RecipeUrlForm';
import { useQueryClient } from '@tanstack/react-query';

const AddRecipes = () => {
  const queryClient = useQueryClient();
  const [isImporting, setIsImporting] = useState(false);

  const handleRecipeAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['recipes'] });
  };

  const handleLoadingChange = (loading: boolean) => {
    setIsImporting(loading);
  };

  return (
    <PageLayout 
      title="Add Recipes" 
      description="Choose how you'd like to add recipes to your collection"
    >
      <div className="max-w-4xl mx-auto pb-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Import from URL */}
          <Card className="flex flex-col">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl">Import from URL</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-muted-foreground text-center mb-4 flex-1">
                Import recipes from recipe websites by entering the URL
              </p>
              <RecipeUrlForm 
                onRecipeAdded={handleRecipeAdded} 
                variant="inline" 
                onLoadingChange={handleLoadingChange}
              />
            </CardContent>
          </Card>

          {/* Add Quick Recipe */}
          <Card className={`flex flex-col transition-opacity ${isImporting ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 bg-sage/10 rounded-full flex items-center justify-center mb-3">
                <Plus className="h-6 w-6 text-sage" />
              </div>
              <CardTitle className="text-xl">Add Quick Recipe</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-muted-foreground text-center mb-4 flex-1">
                Create a simple recipe with minimal effort using our quick form
              </p>
              <Button asChild className="w-full">
                <Link to="/create-quick-recipe">
                  Create Quick Recipe
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Find Recipe */}
          <Card className={`flex flex-col transition-opacity ${isImporting ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 bg-sage/10 rounded-full flex items-center justify-center mb-3">
                <Search className="h-6 w-6 text-sage" />
              </div>
              <CardTitle className="text-xl">Find Recipe</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-muted-foreground text-center mb-4 flex-1">
                Search for recipes online and save your favorites to your collection
              </p>
              <Button asChild className="w-full">
                <Link to="/recipe-finder">
                  Find Recipes
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Create Composition */}
          <Card className={`flex flex-col transition-opacity ${isImporting ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 bg-sage/10 rounded-full flex items-center justify-center mb-3">
                <Users className="h-6 w-6 text-sage" />
              </div>
              <CardTitle className="text-xl">Create Composition</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-muted-foreground text-center mb-4 flex-1">
                Group multiple recipes together for coordinated meal planning
              </p>
              <Button asChild className="w-full">
                <Link to="/create-composition">
                  Create Composition
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default AddRecipes;
